import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { PaperClipIcon, ArrowDownTrayIcon, TrashIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import BarcodeScanner from '../ui/BarcodeScanner';

const ConsignmentModal = ({
  selectedClient,
  consignment,
  clients,
  onSave,
  onClose,
  /* doc handlers — only provided when consignment is already saved (id > 0) */
  onUploadConsignmentDocs,
  onDownloadConsignmentDoc,
  onDeleteConsignmentDoc,
  tripSaved,
}) => {
  // The client is always locked to the trip's selected client.
  // For a new consignment: use selectedClient (the trip's client_id).
  // For an edit: keep the consignment's existing client (shouldn't differ,
  // but we respect whatever was saved).
  const lockedClientId = consignment?.client || selectedClient || (clients.length > 0 ? clients[0]?.id : 0);
  const lockedClient   = clients.find(c => String(c.id) === String(lockedClientId)) || (clients.length > 0 ? clients[0] : null);

  const createInitialForm = () => ({
    id:           consignment?.id       || 0,
    tmp_id:       consignment?.tmp_id   || uuidv4(),
    client:       lockedClient?.id      || 0,
    client_name:  lockedClient?.name    || consignment?.client_name || '',
    hwb_number:   consignment?.hwb_number  || '',
    tms_id:       consignment?.tms_id      || '',
    total_boxes:  consignment?.total_boxes  || '',
    total_weight: consignment?.total_weight || '',
    remarks:      consignment?.remarks      || '',
  });

  const [form, setForm] = useState(createInitialForm);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Camera barcode scanner state — mobile only
  const [showScanner, setShowScanner] = useState(false);
  // Which field the scanner result goes into: 'hwb' | 'tms'
  const [scanTarget, setScanTarget] = useState('hwb');

  const docDesktopRef = useRef(null);
  const docMobileRef = useRef(null);

  const triggerDocInput = () => {
    if (window.innerWidth < 768) docMobileRef.current?.click();
    else docDesktopRef.current?.click();
  };

  const handleDocFilePicked = (e) => {
    const picked = Array.from(e.target.files);
    setStagedFiles((prev) => [...prev, ...picked]);
    e.target.value = '';
  };

  // 'hwb' | 'tms' | null — HID scanner indicator
  const [scanField, setScanField] = useState(null);

  const hwbInputRef = useRef(null);
  const tmsInputRef = useRef(null);

  const hwbLastKeyTimeRef = useRef(0);
  const hwbScanCharCountRef = useRef(0);
  const hwbWasScannedRef = useRef(false);
  const tmsLastKeyTimeRef = useRef(0);
  const tmsScanCharCountRef = useRef(0);
  const tmsWasScannedRef = useRef(false);

  const scanTimerRef = useRef(null);

  useEffect(() => {
    hwbInputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => { if (scanTimerRef.current) clearTimeout(scanTimerRef.current); };
  }, []);

  const showScanIndicator = (fieldKey) => {
    setScanField(fieldKey);
    clearTimeout(scanTimerRef.current);
    scanTimerRef.current = setTimeout(() => setScanField(null), 1500);
  };

  const handleStartHwbScan = () => {
    hwbScanCharCountRef.current = 0;
    hwbLastKeyTimeRef.current = 0;
    hwbInputRef.current?.focus();
  };

  const handleStartTmsScan = () => {
    tmsScanCharCountRef.current = 0;
    tmsLastKeyTimeRef.current = 0;
    tmsInputRef.current?.focus();
  };

  const handleHwbKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (hwbScanCharCountRef.current >= 4) {
        e.preventDefault();
        e.stopPropagation();
        hwbWasScannedRef.current = true;
        showScanIndicator('hwb');
        hwbInputRef.current?.focus();
      }
      hwbScanCharCountRef.current = 0;
      hwbLastKeyTimeRef.current = 0;
      return;
    }
    const prevTime = hwbLastKeyTimeRef.current;
    const gap = Date.now() - prevTime;
    hwbLastKeyTimeRef.current = Date.now();
    if (prevTime !== 0 && gap <= 100) {
      hwbScanCharCountRef.current += 1;
    } else {
      if (hwbWasScannedRef.current) {
        setForm((prev) => ({ ...prev, hwb_number: '' }));
        hwbWasScannedRef.current = false;
      }
      hwbScanCharCountRef.current = 1;
    }
  };

  const handleTmsKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (tmsScanCharCountRef.current >= 4) {
        e.preventDefault();
        e.stopPropagation();
        tmsWasScannedRef.current = true;
        showScanIndicator('tms');
        tmsInputRef.current?.focus();
      }
      tmsScanCharCountRef.current = 0;
      tmsLastKeyTimeRef.current = 0;
      return;
    }
    const prevTime = tmsLastKeyTimeRef.current;
    const gap = Date.now() - prevTime;
    tmsLastKeyTimeRef.current = Date.now();
    if (prevTime !== 0 && gap <= 100) {
      tmsScanCharCountRef.current += 1;
    } else {
      if (tmsWasScannedRef.current) {
        setForm((prev) => ({ ...prev, tms_id: '' }));
        tmsWasScannedRef.current = false;
      }
      tmsScanCharCountRef.current = 1;
    }
  };

  // ── Camera scanner handlers ─────────────────────────────────────────────────

  const openCameraScanner = (target) => {
    setScanTarget(target);
    setShowScanner(true);
  };

  const handleCameraScan = (value) => {
    setShowScanner(false);
    if (scanTarget === 'hwb') {
      setForm((prev) => ({ ...prev, hwb_number: value }));
      showScanIndicator('hwb');
    } else {
      setForm((prev) => ({ ...prev, tms_id: value }));
      showScanIndicator('tms');
    }
    // Focus back to the field after scanner closes
    setTimeout(() => {
      if (scanTarget === 'hwb') hwbInputRef.current?.focus();
      else tmsInputRef.current?.focus();
    }, 100);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // 'client' is locked to the trip's selected client — ignore any change attempts.
    if (name === 'client') return;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForNewConsignment = () => {
    setForm((prev) => ({
      ...prev,
      id: 0,
      tmp_id: uuidv4(),
      hwb_number: '',
      tms_id: '',
      total_boxes: '',
      total_weight: '',
      remarks: ''
    }));
    hwbScanCharCountRef.current = 0;
    tmsScanCharCountRef.current = 0;
    hwbWasScannedRef.current = false;
    tmsWasScannedRef.current = false;
    setTimeout(() => hwbInputRef.current?.focus(), 0);
  };

  const handleSave = async () => {
    const normalizedBoxes = String(form.total_boxes || '').trim().replace(/\s+/g, '');
    if (!form.client || !form.hwb_number || !normalizedBoxes || !form.total_weight) {
      alert('Please fill all fields.');
      return;
    }
    if (!/^\d+(\/\d+)?$/.test(normalizedBoxes)) {
      alert('Total Boxes should be in format like 1, 10, 1/10, or 5/10.');
      return;
    }
    try {
      setIsSaving(true);
      await onSave({ ...form, total_boxes: normalizedBoxes, _stagedFiles: stagedFiles });
      if (consignment) {
        if (stagedFiles.length > 0 && consignment.id > 0 && tripSaved && onUploadConsignmentDocs) {
          await onUploadConsignmentDocs(consignment.id, stagedFiles);
          setStagedFiles([]);
        }
        onClose();
      } else {
        setStagedFiles([]);
        resetForNewConsignment();
      }
    } catch (e) {
      console.error('Error saving consignment: ', e);
      toast.error('Error saving consignment.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnterSubmit = (e) => {
    if (e.key !== 'Enter') return;
    const targetName = e.target?.name || '';
    const targetTag = (e.target?.tagName || '').toLowerCase();
    if (targetName === 'hwb_number' || targetName === 'tms_id') return;
    if (targetTag === 'textarea' || targetTag === 'button' || targetName === 'remarks') return;
    e.preventDefault();
    handleSave();
  };

  // HID scan button style (desktop)
  const scanBtnClass = 'hidden md:inline-flex px-2 py-1 text-xs rounded border font-medium bg-gray-100 text-gray-600 border-gray-300 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 transition-colors';

  return (
    <>
      {/* ── Camera scanner overlay (mobile only) ── */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleCameraScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      <div className="fixed inset-0 bg-white/40 backdrop-blur-sm flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded shadow w-full max-w-md border border-gray-300 max-h-[90dvh] overflow-y-auto">
          <h2 className="text-lg font-bold mb-4">
            {consignment ? 'Edit Consignment' : 'Add Consignment'}
          </h2>

          <div onKeyDown={handleEnterSubmit}>
            {/* Client — read-only, locked to the trip's selected client */}
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">Client</label>
              <div className="w-full border border-gray-200 rounded p-2 text-sm bg-gray-50 text-gray-700 select-none">
                {form.client_name || '—'}
              </div>
            </div>

            {/* HWB Number */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-gray-600">HWB/Invoice Number</label>
                {/* Desktop: HID scan button */}
                <button type="button" onClick={handleStartHwbScan} className={scanBtnClass}>
                  📷 Scan
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="hwb_number"
                  value={form.hwb_number}
                  onChange={handleInputChange}
                  onKeyDown={handleHwbKeyDown}
                  placeholder="HWB Number"
                  className={`flex-1 border rounded p-2 text-sm ${scanField === 'hwb' ? 'border-green-500' : 'border-gray-300'}`}
                  ref={hwbInputRef}
                />
                {/* Mobile: camera scan button */}
                <button
                  type="button"
                  onClick={() => openCameraScanner('hwb')}
                  className="md:hidden flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-xs font-medium shrink-0"
                  title="Scan barcode with camera"
                >
                  <QrCodeIcon className="h-4 w-4" />
                  Scan
                </button>
              </div>
              {scanField === 'hwb' && (
                <span className="text-green-600 text-xs font-medium mt-1 inline-block">✓ Scanned</span>
              )}
            </div>

            {/* TMS ID */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-gray-600">TMS ID</label>
                {/* Desktop: HID scan button */}
                <button type="button" onClick={handleStartTmsScan} className={scanBtnClass}>
                  📷 Scan
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="tms_id"
                  value={form.tms_id}
                  onChange={handleInputChange}
                  onKeyDown={handleTmsKeyDown}
                  placeholder="TMS ID"
                  className={`flex-1 border rounded p-2 text-sm ${scanField === 'tms' ? 'border-green-500' : 'border-gray-300'}`}
                  ref={tmsInputRef}
                />
                {/* Mobile: camera scan button */}
                <button
                  type="button"
                  onClick={() => openCameraScanner('tms')}
                  className="md:hidden flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-xs font-medium shrink-0"
                  title="Scan barcode with camera"
                >
                  <QrCodeIcon className="h-4 w-4" />
                  Scan
                </button>
              </div>
              {scanField === 'tms' && (
                <span className="text-green-600 text-xs font-medium mt-1 inline-block">✓ Scanned</span>
              )}
            </div>

            {/* Total Boxes */}
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">Total Boxes</label>
              <input
                type="text"
                name="total_boxes"
                value={form.total_boxes}
                onChange={handleInputChange}
                placeholder="Total Boxes (e.g. 1/10)"
                className="w-full border border-gray-300 rounded p-2 text-sm"
              />
            </div>

            {/* Total Weight */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">Total Weight (In Kgs)</label>
              <input
                type="number"
                name="total_weight"
                value={form.total_weight}
                min={0}
                onChange={handleInputChange}
                placeholder="Total Weight"
                className="w-full border border-gray-300 rounded p-2 text-sm"
              />
            </div>

            {/* Remarks */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">Remarks</label>
              <textarea
                name="remarks"
                value={form.remarks}
                onInput={handleInputChange}
                onChange={handleInputChange}
                placeholder="Remarks"
                className="w-full border border-gray-300 rounded p-2 text-sm"
                rows={3}
              />
            </div>

            {/* Documents */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1 font-semibold">Documents</label>

              {consignment && consignment.id > 0 && (consignment.documents || []).length > 0 && (
                <ul className="mb-2 space-y-1">
                  {(consignment.documents || []).map((doc) => (
                    <li key={doc.id} className="flex items-center gap-2 text-xs">
                      <PaperClipIcon className="h-3 w-3 text-gray-400 shrink-0" />
                      <span
                        className="truncate max-w-[180px] text-blue-600 cursor-pointer hover:underline"
                        title={doc.original_filename}
                        onClick={() => onDownloadConsignmentDoc?.(doc.id, doc.original_filename)}
                      >
                        {doc.original_filename}
                      </span>
                      <button
                        type="button"
                        title="Delete"
                        className="text-red-400 hover:text-red-600"
                        onClick={() => onDeleteConsignmentDoc?.(doc.id)}
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {stagedFiles.length > 0 && (
                <ul className="mb-2 space-y-1">
                  {stagedFiles.map((f, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs text-gray-500">
                      <PaperClipIcon className="h-3 w-3 shrink-0" />
                      <span className="truncate max-w-[180px]">{f.name}</span>
                      <button
                        type="button"
                        className="text-red-400 hover:text-red-600"
                        onClick={() => setStagedFiles((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {tripSaved ? (
                <>
                  <input type="file" accept="application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*" multiple ref={docDesktopRef} className="hidden" onChange={handleDocFilePicked} />
                  <input type="file" accept="image/*" capture="environment" ref={docMobileRef} className="hidden" onChange={handleDocFilePicked} />
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 border border-blue-300 rounded px-2 py-1"
                    onClick={triggerDocInput}
                  >
                    <ArrowDownTrayIcon className="h-3 w-3 rotate-180" />
                    Attach files
                  </button>
                  {!consignment?.id && (
                    <p className="text-xs text-gray-400 mt-1">Files will be uploaded after the consignment is saved.</p>
                  )}
                  {consignment?.id > 0 && stagedFiles.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1">Files will be uploaded when you click Save.</p>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-400 italic">Save the trip first to attach documents.</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    hwbInputRef.current?.focus();
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {isSaving ? 'Saving…' : (consignment ? 'Save' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsignmentModal;
